/**
 * Shared challenge-eligibility logic. Both LadderPage and PlayerPage import
 * from here so the two surfaces can never disagree on whether the "Challenge"
 * affordance should appear.
 *
 * Inputs are intentionally tiny so callers can pass plain ladder rows.
 *
 * `viewerRow` and `targetRow` are rows from /api/ladder (or shaped like them).
 * They carry the only state we need: rank, status, and any `outgoing_challenge`
 * / `incoming_challenge` markers.
 */

/**
 * True when the viewer-target pair is already in a live challenge together.
 */
export function sharesActiveChallenge(viewerRow, targetRow) {
  if (!viewerRow || !targetRow) return false;
  const out = viewerRow.outgoing_challenge;
  const inc = viewerRow.incoming_challenge;
  if (out && out.opponent_id === targetRow.player_id) return true;
  if (inc && inc.opponent_id === targetRow.player_id) return true;
  return false;
}

/**
 * True when the rank rules alone allow a challenge between viewer → target
 * (ignoring blocker checks).
 *
 * Post-launch nearest-4-active rule: paused (or otherwise non-active)
 * players still occupy their ranks but do NOT consume one of the four
 * challenge slots. We walk `allRows` upward from the viewer and only
 * count ranks whose player is `status === 'active'`. If `allRows` is
 * omitted, we fall back to the simple rank-distance check so any legacy
 * caller keeps working (defensive over-restrictive, never over-permissive).
 */
export function targetInRangeFor(viewerRow, targetRow, allRows = null) {
  if (!viewerRow || !targetRow) return false;
  if (viewerRow.status !== 'active') return false;
  if (targetRow.status !== 'active') return false;
  if (viewerRow.player_id === targetRow.player_id) return false;
  if (!viewerRow.rank || targetRow.rank >= viewerRow.rank) return false;
  if (Array.isArray(allRows) && allRows.length) {
    const eligibleIds = computeChallengeableIds(viewerRow, allRows);
    return eligibleIds.has(targetRow.player_id);
  }
  // Fallback — legacy simple rank-distance check.
  if (viewerRow.rank - targetRow.rank > 4) return false;
  return true;
}

/**
 * Returns a Set<player_id> of the nearest 4 active players ranked above
 * the viewer. Mirrors the backend `_nearest_active_player_ids_above`
 * helper so the two surfaces cannot disagree.
 */
export function computeChallengeableIds(viewerRow, allRows, needed = 4) {
  const out = new Set();
  if (!viewerRow || !viewerRow.rank || !Array.isArray(allRows)) return out;
  const above = allRows
    .filter((r) => r && r.rank && r.rank < viewerRow.rank)
    .sort((a, b) => b.rank - a.rank); // nearest (highest rank number) first
  for (const r of above) {
    if (r.status === 'active') {
      out.add(r.player_id);
      if (out.size >= needed) break;
    }
  }
  return out;
}

/**
 * Single source of truth: returns true ONLY when the viewer may currently
 * issue a brand-new challenge to `targetRow`. Encodes the full priority chain:
 *   - viewer must be active
 *   - target must be active and within 4-rank range above viewer
 *   - viewer must NOT already have any outgoing or incoming challenge
 *   - target must NOT already be locked into a challenge with anyone
 *   - they must not already share a challenge (that case is "View Match", not "Challenge")
 *
 * Callers should hide the Challenge button entirely when this is false.
 */
export function canChallengeTarget(viewerRow, targetRow, allRows = null) {
  if (!viewerRow || !targetRow) return false;
  // Already share a challenge → not a "new challenge" opportunity.
  if (sharesActiveChallenge(viewerRow, targetRow)) return false;
  if (!targetInRangeFor(viewerRow, targetRow, allRows)) return false;
  // Viewer is locked up by ANY active challenge (outgoing OR incoming).
  if (viewerRow.outgoing_challenge || viewerRow.incoming_challenge) return false;
  // Target is locked up by their own challenge with someone else.
  const targetBlock = targetRow.incoming_challenge || targetRow.outgoing_challenge;
  if (targetBlock) return false;
  return true;
}
